import { useState, useEffect, useRef, useMemo } from 'react';
import {
  Trophy,
  ChevronRight,
  ArrowRight,
  Crown,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';

import '@/App.css';
import success_1 from '@/assets/success_1.jpeg';
import success_2 from '@/assets/success_2.jpeg';
import success_3 from '@/assets/success_3.jpeg';
import company from '@/assets/company.jpeg';

import highlight_1 from '@/assets/highlight_1.jpeg';

import Navigation from '@/components/nav';
import FooterSection from '@/components/footer';

const API_BASE = import.meta.env.VITE_API_BASE || "https://qwaqwatalentshow.co.za";;

interface HighlightItem {
  id: number;
  title: string;
  date: string;
  description: string;
  thumbnail: string;
  attendance: number;
  category_id: string;
  featured: boolean;
}

// Types
interface Contestant {
  id: number;
  name: string;
  image: string;
  tagline: string;
  votes: number;
  rank: number;
  category: string;
  bio: string;
  achievements: string[];
  stats: {
    visits: number;
    engagement: number;
    trending: boolean;
  };
}

// Data
const contestants: Contestant[] = [
  {
    id: 1,
    name: "Kaydii M",
    image: success_1,
    tagline: "Redefining excellence through artistry",
    votes: 15420,
    rank: 1,
    category: "Performance",
    bio: "Elena is a world-class performer with over 10 years of experience on international stages. Her unique blend of classical training and contemporary innovation has captivated audiences worldwide.",
    achievements: ["Gold Medalist 2024", "Best Performer Award", "Audience Choice Winner"],
    stats: { visits: 5500, engagement: 94, trending: true }
  },
  {
    id: 2,
    name: "Thunder Strikers FC",
    image: success_2,
    tagline: "United in passion, unstoppable in victory",
    votes: 12850,
    rank: 2,
    category: "Football",
    bio: "Thunder Strikers FC is a dynamic soccer team known for their electrifying performances and unstoppable teamwork. Their goal celebrations have become iconic, showcasing unity, energy, and pure love for the game.",
    achievements: [
      "League Champions 2025",
      "Best Team Chemistry Award",
      "Top Scoring Team"
    ],
    stats: { visits: 38100, engagement: 89, trending: true }
  },
  {
    id: 3,
    name: "Storm Breakers FC",
    image: success_3,
    tagline: "Rising through the storm, feared by every opponent",
    votes: 16720,
    rank: 2,
    category: "Football",
    bio: "Storm Breakers FC delivered an unforgettable tournament run, showcasing explosive speed, tactical precision, and relentless determination. Though finishing as finalists, their performances captured the hearts of fans everywhere.",
    achievements: [
      "Tournament Finalists 🥈",
      "Best Defensive Team",
      "Most Dramatic Comeback Match",
      "Fan Favorite Team"
    ],
    stats: { visits: 49700, engagement: 92, trending: true }
  }

];


// Hero Section with White Background
function HeroSection() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <section className="relative h-screen overflow-hidden">

      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src={highlight_1}
          alt="Background"
          className="w-full h-full object-cover"
        />
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-black/60"></div>
      </div>

      {/* Animated Decorative Ribbon SVG */}
      <svg className="absolute top-0 right-0 w-[70vw] h-[100vh] opacity-70 z-0" viewBox="0 0 800 1000" fill="none">
        <path
          className="ribbon-animate"
          d="M600 -50 Q 750 200 500 400 Q 250 600 450 800 Q 650 1000 400 1100"
          stroke="#ff0000"
          strokeWidth="120"
          fill="none"
          strokeLinecap="round"
        />
      </svg>

      {/* Gold Accent Circles */}
      <div className="absolute top-20 left-10 w-32 h-32 rounded-full bg-gradient-to-br from-QWA2_TALENT_SHOW-gold/20 to-QWA2_TALENT_SHOW-gold/5 blur-2xl z-0" />
      <div className="absolute bottom-40 right-20 w-48 h-48 rounded-full bg-gradient-to-br from-QWA2_TALENT_SHOW-red/10 to-QWA2_TALENT_SHOW-gold/10 blur-3xl z-0" />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 md:pt-32 pb-16">
        <div className="grid lg:grid-cols-1 gap-8 lg:gap-12 items-center min-h-[80vh]">



          {/* RIGHT SIDE CONTENT */}
          <div className={`space-y-6 md:space-y-8 transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`}>

            <div>

              <h1 className="font-black text-5xl md:text-7xl lg:text-8xl text-white leading-[0.9] tracking-tight">
                Welcome<br />
                <span className="bg-gradient-to-r from-yellow-400 to-red-500 bg-clip-text text-transparent">
                  Dumelang!<br/>Re a le amohela
                </span>
              </h1>
            </div>

            <p className="text-lg md:text-xl text-white/80 max-w-lg">
              Meet the finalists. Celebrate their stories. Cast your vote and decide who takes the crown.
            </p>

            {/* <div className="flex flex-wrap gap-4">
              <Button
                size="lg"
                className="
    group
    relative
    flex items-center
    bg-gradient-to-r from-red-600 via-red-500 to-red-700
    text-white
    font-semibold
    uppercase tracking-wider
    px-8 py-3
    rounded-2xl
    shadow-lg
    hover:shadow-red-500/40
    transition-all duration-300 ease-in-out
    hover:scale-105
    active:scale-95
  "
              >
                <span className="z-10">Competitions</span>

                <ChevronRight
                  className="
      w-5 h-5 ml-2
      transition-transform duration-300
      group-hover:translate-x-1
    "
                />

               
                <span
                  className="
      absolute inset-0
      rounded-2xl
      bg-red-500 opacity-0
      group-hover:opacity-20
      blur-xl
      transition duration-300
    "
                />
              </Button>
            </div> */}

          </div>
        </div>
      </div>
    </section>
  );
}


// Spotlight Section
function SpotlightSection({
  contestant
}: {
  contestant: Contestant[];
}) {
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [index, setIndex] = useState(0);

  const current = contestant[index];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  // auto slide
  useEffect(() => {
    const interval = setInterval(() => {
      setIndex((prev) => (prev + 1) % contestant.length);
    }, 6000);

    return () => clearInterval(interval);
  }, [contestant.length]);

  return (
    <section
      ref={sectionRef}
      className="relative min-h-screen bg-gray-50 overflow-hidden"
    >
      {/* Ribbon */}
      <svg
        className="absolute right-[20vw] top-0 w-[70vw] h-[90vh] opacity-70"
        viewBox="0 0 500 800"
        fill="none"
      >
        <path
          className="ribbon-animate"
          d="M400 200 A200 200 0 1 1 399 200"
          stroke="#ff0000"
          strokeWidth="100"
          fill="none"
          strokeLinecap="round"
        />
      </svg>

      {/* Accent */}
      <div className="absolute left-20 top-40 w-40 h-40 rounded-full bg-QWA2_TALENT_SHOW-gold/10 blur-3xl" />

      {/* SLIDE WRAPPER */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">

        <div
          key={current.id}
          className={`grid lg:grid-cols-2 gap-8 items-center min-h-[80vh] transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]
            ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-y-10'}
          `}
        >

          {/* IMAGE */}
          <div
            className={`relative mask-bubble-v2
    ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-105'}
  `}
          >
            <img
              src={current.image}
              alt={current.name}
              className="w-full h-[60vh] lg:h-[80vh] object-cover"
            />

            <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
          </div>

          {/* CONTENT */}
          <div className="space-y-6 lg:order-1">

            <div className="transition-all duration-500">
              <span className="uppercase tracking-widest text-QWA2_TALENT_SHOW-gold text-sm font-medium">
                Finalist — 2026
              </span>
            </div>

            <h2 className="font-black text-5xl md:text-7xl lg:text-8xl leading-[0.9]">
              <span className="relative inline-block">

                <span
                  className="absolute inset-0"
                  style={{
                    WebkitTextStroke: '2px black',
                    color: 'transparent',
                  }}
                >
                  {current.name.toUpperCase()}
                </span>

                <span className="red-gold-gradient-text relative">
                  {current.name.toUpperCase()}
                </span>

              </span>
            </h2>

            <p className="text-xl text-QWA2_TALENT_SHOW-gray max-w-md">
              {current.tagline}
            </p>

            <p className="text-lg text-QWA2_TALENT_SHOW-gray max-w-md">
              Category: {current.category}
            </p>

            {/* <div className="flex gap-4">
              <Button
                variant="link"
                className="text-QWA2_TALENT_SHOW-gold hover:text-QWA2_TALENT_SHOW-red p-0 animate-color-shift"
              >
                View profile <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div> */}

          </div>
        </div>

        {/* DOT NAVIGATION */}
        <div className="flex justify-center gap-3 mt-10">
          {contestant.map((_, i) => (
            <button
              key={i}
              onClick={() => setIndex(i)}
              className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${i === index
                  ? 'bg-QWA2_TALENT_SHOW-gold scale-125'
                  : 'bg-gray-300'
                }`}
            />
          ))}
        </div>

      </div>
    </section>
  );
}

// Trophy Section
function TrophySection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="relative min-h-screen bg-white overflow-hidden">
      {/* Animated Ribbon Wrap */}
      <svg
        className="absolute top-0 left-1/2 -translate-x-1/2 opacity-60"
        style={{ maxHeight: "150vh", width: "900px" }}
        viewBox="0 0 1000 1000"
        fill="none"
        preserveAspectRatio="xMidYMid slice"
      >
        <path
          className="ribbon-animate"
          d="M200 100 Q 500 0 800 200 Q 1000 500 800 800 Q 500 1000 200 800 Q 0 500 200 100"
          stroke="#ff0000"
          strokeWidth="150"
          fill="none"
          strokeLinecap="round"
        />
      </svg>

      {/* Gold Accents */}
      <div className="absolute top-20 left-20 w-48 h-48 rounded-full bg-QWA2_TALENT_SHOW-gold/10 blur-3xl" />
      <div className="absolute bottom-20 right-20 w-32 h-32 rounded-full bg-QWA2_TALENT_SHOW-gold/15 blur-2xl" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center min-h-[80vh]">
          <div className="space-y-6">
            <h2
              className={`font-black text-5xl md:text-7xl text-QWA2_TALENT_SHOW-black leading-tight transition-all duration-700 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'
                }`}
            >
              THE AWARD<br />
              <span className="red-gold-gradient-text">AWAITS</span>
            </h2>
            <p
              className={`text-lg text-QWA2_TALENT_SHOW-gray max-w-md transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
                }`}
            >
              One winner. One night. Your vote helps crown the next champion—live on stage.
            </p>
            <div
              className={`flex gap-4 transition-all duration-700 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
            >
              <Button size="lg" className="animate-bg-color-shift text-white uppercase tracking-wider animate-pulse-glow hover:opacity-90">
                <Trophy className="w-5 h-5 mr-2" /> Cast Your Vote
              </Button>
              <Button size="lg" variant="outline" className="border-2 border-QWA2_TALENT_SHOW-gold text-QWA2_TALENT_SHOW-gold hover:bg-QWA2_TALENT_SHOW-gold hover:text-white">
                See the Rules
              </Button>
            </div>
          </div>

          <div
            className={`relative transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 translate-y-12'
              }`}
          >
            <div className="relative rounded-3xl overflow-hidden shadow-card border-2 border-transparent animate-border-color-shift">
              <img
                src={company}
                alt="QWA2 TALENT SHOW Award Trophy"
                className="w-full h-[50vh] lg:h-[70vh] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
              <div className="absolute bottom-8 left-8 right-8">
                <Badge className="bg-QWA2_TALENT_SHOW-gold text-white mb-2">
                  <Crown className="w-3 h-3 mr-1" /> Grand Prize
                </Badge>
                <h3 className="text-white text-2xl font-bold">The QWA2 TALENT SHOW Trophy</h3>
                <p className="text-white/80 text-sm">Awarded to the People's Choice Winner</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <HighlightsSection />
    </section>
  );
}



// Highlights Section
function HighlightsSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [highlights, setHighlights] = useState<HighlightItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHighlights = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${API_BASE}/api/v1/highlights`);
        const data = await res.json();
        if (Array.isArray(data)) {
          // Sort by date in descending order and take the latest 6
          const sortedHighlights = data
            .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
            .slice(0, 6);
          setHighlights(sortedHighlights);
        }
      } catch (err) {
        console.error("Failed to fetch highlights:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchHighlights();
  }, []);

  const displayedHighlights = useMemo(() => {
    return highlights.map(h => ({
      ...h,
      image: h.thumbnail.startsWith('http') ? h.thumbnail : `${API_BASE}${h.thumbnail}`,
      year: h.date.split('-')[0]
    }));
  }, [highlights]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="highlights" ref={sectionRef} className="py-20 md:py-32 bg-gray-50 relative overflow-hidden">
      {/* Gold Accents */}
      <div className="absolute top-20 left-20 w-32 h-32 rounded-full bg-QWA2_TALENT_SHOW-gold/10 blur-3xl" />
      <div className="absolute bottom-20 right-20 w-40 h-40 rounded-full bg-QWA2_TALENT_SHOW-gold/10 blur-3xl" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`mb-12 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h2 className="font-black text-4xl md:text-6xl text-QWA2_TALENT_SHOW-black">
            MOMENTS THAT<br />
            <span className="red-gold-gradient-text">MATTER</span>
          </h2>
          <p className="mt-4 text-QWA2_TALENT_SHOW-gray text-lg max-w-xl">
            Behind the scenes, on-stage magic, and the crowd's energy—captured in frames.
          </p>
        </div>

        {loading ? (
          <div className="text-center py-10">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-QWA2_TALENT_SHOW-gold mx-auto"></div>
            <p className="mt-4 text-gray-500">Loading highlights...</p>
          </div>
        ) : displayedHighlights.length === 0 ? (
          <div className="text-center py-10">
            <p className="text-gray-500 text-lg font-medium">No highlights to display yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
            {displayedHighlights.map((highlight, index) => (
              <Dialog key={highlight.id}>
                <DialogTrigger asChild>
                  <div
                    className={`group relative overflow-hidden rounded-2xl cursor-pointer shadow-card hover:shadow-card-hover transition-all duration-500 hover:-translate-y-1 border-2 border-transparent hover:border-QWA2_TALENT_SHOW-gold/50 ${index === 0 || index === 3 ? 'col-span-2 row-span-2' : ''
                      } ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
                    style={{ transitionDelay: `${index * 100}ms` }}
                  >
                    <img
                      src={highlight.image}
                      alt={highlight.title}
                      className={`w-full object-cover transition-transform duration-700 group-hover:scale-110 ${index === 0 || index === 3 ? 'h-80 md:h-96' : 'h-40 md:h-48'
                        }`}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                    <div className="absolute bottom-4 left-4 right-4">
                      <Badge className="bg-QWA2_TALENT_SHOW-gold text-white mb-2">{highlight.year}</Badge>
                      <h3 className="text-white font-bold text-lg">{highlight.title}</h3>
                      <p className="text-white/70 text-sm">{highlight.description}</p>
                    </div>
                  </div>
                </DialogTrigger>
                <DialogContent className="max-w-3xl">
                  <DialogHeader>
                    <DialogTitle className="red-gold-gradient-text">{highlight.title}</DialogTitle>
                  </DialogHeader>
                  <img src={highlight.image} alt={highlight.title} className="w-full h-96 object-cover rounded-xl" />
                  <p className="text-QWA2_TALENT_SHOW-gray">{highlight.description}</p>
                </DialogContent>
              </Dialog>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}



const Home = () => {
  // Main App
  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      <main>
        <HeroSection />
        {/* <ContestantsSection /> */}
        <SpotlightSection contestant={contestants} />
        <TrophySection />

      </main>
      <FooterSection />
    </div>
  );
};

export default Home;
